import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn, ShieldAlert, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { login } = useAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setError('');
    setIsLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
      return;
    }
    setIsLoading(false);
    setPassword('');
    setError(result.error || 'Invalid email or password.');
  };

  const closeError = () => setError('');

  // Close the popup with Escape, like any dialog
  useEffect(() => {
    if (!error) return undefined;
    const onKey = (e) => e.key === 'Escape' && closeError();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [error]);

  return (
    <div className="relative min-height-screen min-h-screen flex items-center justify-center bg-[#070a13] overflow-hidden px-4">
      {/* Dynamic Drifting Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[450px] h-[450px] rounded-full bg-indigo-500/10 blur-[120px] animate-float-1" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-purple-500/10 blur-[100px] animate-float-2" />
      <div className="absolute top-[30%] right-[20%] w-[250px] h-[250px] rounded-full bg-cyan-500/5 blur-[80px] animate-float-1" />

      {/* Main Glass Login Container */}
      <div className="relative w-full max-w-md z-10">

        {/* Upper Header Brand logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-xl shadow-indigo-500/20 mb-3 border border-white/15 hover:scale-105 transition-transform duration-300">
            <svg viewBox="0 0 40 40" width="32" height="32" fill="none">
              <rect x="3" y="5" width="23" height="16" rx="5" fill="#BFFF4A" />
              <path d="M9 21 L6 28 L15 21Z" fill="#BFFF4A" />
              <rect x="14" y="16" width="23" height="16" rx="5" fill="white" fillOpacity=".9" />
            </svg>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-indigo-200 to-indigo-100 bg-clip-text text-transparent">
            Ryzz Admin Panel
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Secure administrative authorization portal
          </p>
        </div>

        {/* Card Panel */}
        <div className="glass-panel rounded-2xl p-8 shadow-2xl relative border-white/[0.08] animate-border-glow">

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
                Admin Email
              </label>
              <div className="relative group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-400 transition-colors">
                  <Mail className="w-4.5 h-4.5" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-900/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/70 focus:ring-1 focus:ring-indigo-500/30 transition-all"
                  placeholder="admin@ryzz.com"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
                Passphrase
              </label>
              <div className="relative group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-400 transition-colors">
                  <Lock className="w-4.5 h-4.5" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 bg-slate-900/60 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/70 focus:ring-1 focus:ring-indigo-500/30 transition-all"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-indigo-300 transition-colors disabled:cursor-not-allowed"
                >
                  {showPassword ? (
                    <EyeOff className="w-4.5 h-4.5" />
                  ) : (
                    <Eye className="w-4.5 h-4.5" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl flex items-center justify-center gap-2.5 shadow-lg shadow-indigo-500/10 hover:shadow-indigo-500/25 transform active:scale-[0.98] transition-all duration-150 mt-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Authorized Access
                </>
              )}
            </button>
          </form>

          {/* Quick Autofill Helper for Testing */}
          {/* <div className="mt-6 pt-5 border-t border-white/5 text-center">
            <button
              type="button"
              onClick={handleAutofill}
              className="group text-xs text-indigo-300/80 hover:text-indigo-200 bg-indigo-500/5 hover:bg-indigo-500/10 border border-indigo-500/20 px-3.5 py-2.5 rounded-xl transition-all duration-200 inline-flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping group-hover:bg-indigo-300 shrink-0" />
              <span>Autofill Mock Credentials</span>
            </button>
          </div> */}

        </div>

      </div>

      {/* Login error popup */}
      {error && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={closeError}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="login-error-title"
            aria-describedby="login-error-message"
            onClick={(e) => e.stopPropagation()}
            className="glass-panel w-full max-w-sm rounded-2xl p-6 border border-rose-500/30 shadow-2xl text-center space-y-4"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
              <ShieldAlert className="w-6 h-6 text-rose-400" />
            </div>
            <div className="space-y-1.5">
              <h2 id="login-error-title" className="text-lg font-black text-white">Sign-in failed</h2>
              <p id="login-error-message" className="text-sm text-slate-300">{error}</p>
            </div>
            <button
              type="button"
              autoFocus
              onClick={closeError}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm font-semibold"
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
